import React, { useEffect, useState } from 'react';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { useAuth } from '@teko/auth-react';
import { PreLoad } from '../components/Loading';
import '@terra-js/web-bridge';
import TerraKit from '@terra-js/terra-kit';
import { useModal, Body, Modal } from '@teko/ds-mobile';
import { customizedMessage, inIframe } from '@teko/utils';
import dynamic from 'next/dynamic';
import { ApprovalStatus } from '@teko/user';
import { useTenantConfig, useServices } from 'contexts';
import { logger } from '@teko/log';
import { isOver18YearOld } from 'helpers';

// this sucks
// ideally, we should never have to import both mobile and desktop component
// should use router.push instead. Let routing handle that.
const UpdateProfileDesktop = dynamic(
  () => import('components/desktop/pages/UpdateProfilePage'),
);
const UpdateProfileMobile = dynamic(
  () => import('components/mobile/pages/UpdateProfile'),
);
// this component should be phased out
const UpdateInfoForm = dynamic(
  () => import('tenants/_template/desktop/_module/components/UpdateInfoForm'),
);

export interface ForceLoginProps {
  children: React.ReactNode;
}

const BYPASS_LOGIN_PATHS =
  /^.*(\/content\/\[id\])|(\/p\/\[slug\])|(\/landing\/\[id\])$/;

const useConfirmDealerAssignment = (refCode: string) => {
  const { identityRequired } = useTenantConfig();
  const { user } = useAuth();
  const { showModal } = useModal();
  const { userService } = useServices();
  const router = useRouter();

  const onCancelPopup = () => {
    sessionStorage.removeItem('ref_code');
    if (!user?.dealer?.id && identityRequired === 'dealer-required') {
      // if require dealer, signout
      window?.TekoID?.user?.logout();
    } else {
      setLoading(false);
      router.replace('/', undefined, { shallow: true });
    }
  };

  const popup = ({
    title,
    message,
    onOk,
    okText,
    onCancel,
    cancelText,
  }: {
    title?: string;
    message: string;
    okText?: string;
    cancelText?: string;
    onOk?: () => void;
    onCancel?: () => void;
  }) => {
    showModal({
      title: title || t('Thông báo'),
      okText: okText || t('Đóng'),
      cancelText: cancelText || '',
      children: <Body>{message}</Body>,
      onOk: onOk || onCancelPopup,
      onCancel: onCancel || onCancelPopup,
      showCloseIcon: false,
      fullWidthFooter: true,
    });
  };
  const [loading, setLoading] = useState(!!refCode);
  useEffect(() => {
    if (refCode && user) {
      const handleConfirmError = (err: unknown) => {
        sessionStorage.removeItem('ref_code');
        logger.error(err);
        // error can be anything
        // @ts-ignore
        popup({ message: err?.message || err?.response?.message || '' });
      };

      const onAssignDealerSuccess = () => {
        sessionStorage.removeItem('ref_code');
        popup({
          message: t('Đăng ký tài khoản thành công'),
          title: t('Thông báo'),
          okText: t('Đóng'),
          onOk: () => {
            // router.replace('/');
            // setLoading(false);
            // force reload page so that user.dealer data is re-fetched
            window.location.href = '/';
          },
        });
      };

      (async () => {
        try {
          const refCodeInfo = await userService.getRefCodeInfo(refCode);
          const { dealer } = refCodeInfo.data || {};
          if (!dealer) {
            // if there is no dealer,
            throw new Error('Invalid refCode ' + refCode);
          }

          if (user.dealer) {
            if (user.dealer.id === dealer.id) {
              // dealer is already assigned,
              // there is nothing to do
              onCancelPopup();
              return;
            }
            // else confirm if user are ok with the change
            const msg = customizedMessage(
              t('_template.desktop._module.components.IdentityRequired.1'),
              [
                {
                  name: 'oldDealer',
                  value: user.dealer.name || user.dealer.telephone,
                },
                { name: 'newDealer', value: dealer.name || dealer.telephone },
              ],
            );
            popup({
              message: msg,
              title: t('Xác nhận đổi đại lý'),
              okText: t('Đồng ý'),
              cancelText: t('Hủy'),
              onOk: async () => {
                try {
                  await userService.confirmFarmer(refCode);
                  onAssignDealerSuccess();
                } catch (er) {
                  handleConfirmError(er);
                }
              },
              onCancel: onCancelPopup,
            });
          } else {
            await userService.confirmFarmer(refCode);
            onAssignDealerSuccess();
          }
        } catch (er) {
          logger.error('Confirm farmer error code', er);
          popup({
            message: customizedMessage(
              t('Mã giới thiệu {refCode} không hợp lệ'),
              [{ name: 'refCode', value: refCode }],
            ),
          });
          sessionStorage.removeItem('ref_code');
        }
      })();
    }
  }, [refCode, user]);
  return {
    loading,
  };
};

export const ForceLogin: React.FunctionComponent<ForceLoginProps> = ({
  children,
}: ForceLoginProps) => {
  const {
    isMobile,
    identityRequired,
    supportedPhone: hotline,
    requireLogin,
    requiresUserOverEighteenYearsOld,
  } = useTenantConfig();
  const { user, loading, authService, loginWithPartner } = useAuth();
  const router = useRouter();

  const isBypassed =
    BYPASS_LOGIN_PATHS.test(router.route) ||
    router.asPath.includes('/login') ||
    router.asPath.includes('/logout');

  useEffect(() => {
    if (!loading && !user?.userId && !isBypassed) {
      if (!TerraKit.isAvailable()) authService.login();
      else {
        const partnerLogin = async () => {
          const [accessToken, userInfo] = await Promise.all([
            TerraKit.auth().getAccessTokenAsync(),
            TerraKit.auth().getFullUserInfo(),
          ]);
          loginWithPartner(
            {
              partnerType: 'consumerapp',
              userInfo: {
                userId: userInfo.sub,
                name: userInfo.name,
                accessToken,
                phone: userInfo.phoneNumber,
                picture: userInfo.picture,
                email: userInfo.email,
              },
            },
            false,
            false,
          );
        };

        partnerLogin();
      }
    }
  }, [loading, user?.userId, isBypassed]);

  // CP case:
  // todo: move this logic somewhere else. preferrably its own route
  const identityRefCode =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('ref_code')
      : '';
  const { loading: checkingDealer } = useConfirmDealerAssignment(
    identityRefCode || '',
  );

  if (!isBypassed && (loading || !user || checkingDealer)) {
    // iam is not initialized
    // return a loading screen
    return <PreLoad height={'100vh'} isLoading={true} />;
  }

  if (isBypassed) {
    return <>{children}</>;
  }

  // after user login, there are some additional logic
  // like verifying if users have their profiles updated
  const isAccountActive =
    user?.approvalStatus === ApprovalStatus.APPROVED && !!user?.isActive;
  if (!isAccountActive) {
    return isMobile ? <UpdateProfileMobile /> : <UpdateProfileDesktop />;
  }

  return (
    <>
      {children}
      {
        /* bypass check dealer when consumer web is opened in iframe */
        !inIframe() &&
          !user?.dealer?.id &&
          identityRequired === 'dealer-required' && (
            <Modal
              visible={true}
              showCloseIcon={false}
              title={t('Thông báo')}
              okText=""
              onCancel={() => {
                if (requireLogin) {
                  // force logging out in case login is required in the whole site
                  window?.TekoID?.user?.logout();
                } else {
                  // back to previous page when there's one page surrounded by ForceLogin
                  router.back();
                }
              }}
              cancelText={requireLogin ? t('Đăng xuất') : t('Đóng')}
            >
              <Body>
                {customizedMessage(
                  t('_template.desktop._module.components.IdentityRequired.0'),
                  [{ name: 'hotline', value: hotline || '' }],
                )}
              </Body>
            </Modal>
          )
      }
      {/*
        migrate this logic from old code
        TODO:
        - remove UpdateInfoForm, use the UpdateProfilePage component
        - OMNI-890: do not hardcode the check of user info

        We still need to keep this form since mixing forms together makes their logic more complicated :>
       */}
      {requiresUserOverEighteenYearsOld &&
        (!user?.dob || !isOver18YearOld(user.dob)) && <UpdateInfoForm />}
    </>
  );
};
